import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';

import { bitfToTranslate } from '@bitf/utils/bitf-translate.utils';

import {
  ToastMessagesService,
  StoreService,
  BatchService,
  AppSessionService,
  LoaderService,
} from '@services';
import { IBitfApiResponse, IBitfApiAction, IBitfCloseEvent, IBitfJsonBatchRequest } from '@interfaces';
import { EBitfUiMessageType, EBitfCloseEventStatus, CHILD_DELAY_MESSAGE } from '@enums';
import { Child } from '@models';

@Component({
  selector: 'cm-delay-child-dialog',
  templateUrl: './delay-child-dialog.component.html',
  styleUrls: ['./delay-child-dialog.component.scss'],
})
export class DelayChildDialogComponent implements OnInit {
  readonly delayMessage = CHILD_DELAY_MESSAGE;

  childrenList: Child[];

  constructor(
    public storeService: StoreService,
    private dialogRef: MatDialogRef<DelayChildDialogComponent>,
    private loaderService: LoaderService,
    private toastMessagesService: ToastMessagesService,
    private translateService: TranslateService,
    private batchService: BatchService,
    private appSessionService: AppSessionService
  ) {}

  ngOnInit() {
    this.childrenList = this.storeService.store.children.map((child: Child) => new Child(child.clone));
  }

  isDelayed(child: Child): boolean {
    return child.message === this.delayMessage;
  }

  onChangeChildState(child: Child) {
    child.message = this.isDelayed(child) ? '' : this.delayMessage;
  }

  onSaveClick() {
    const childrenToUpdate: Child[] = this.childrenList.filter((childFromList: Child) => {
      const origChild = this.storeService.store.children.find(
        (child: Child) => child.id === childFromList.id
      );
      return origChild.message !== childFromList.message;
    });

    if (!childrenToUpdate.length) {
      this.dialogRef.close({
        data: null,
        status: EBitfCloseEventStatus.CANCEL,
      } as IBitfCloseEvent<any>);
      return;
    }

    const batchRequest: IBitfJsonBatchRequest[] = childrenToUpdate.map((c: Child) => {
      return {
        method: 'PATCH',
        url: `children(${c.id})`,
        body: { message: c.message || '' },
      } as IBitfJsonBatchRequest;
    });

    this.loaderService.show();

    this.batchService
      .jsonBatch(batchRequest)
      .pipe(
        finalize(() => {
          this.appSessionService.triggerPolling$.next();
          this.dialogRef.close({
            data: null,
            status: EBitfCloseEventStatus.OK,
          } as IBitfCloseEvent<any>);
        })
      )
      .subscribe(
        () => {
          const translations = this.getTranslations();

          this.toastMessagesService.show({
            title: translations['COMMON.TOAST_MESSAGES.SUCCESS'],
            message: translations['CHILDREN.DELAY_DIALOG.TM_SUCCESS'],
            type: EBitfUiMessageType.SUCCESS,
          });
        },
        () => {
          console.log('fix batch error');
        }
      );
  }

  private getTranslations() {
    return this.translateService.instant([
      bitfToTranslate('COMMON.TOAST_MESSAGES.SUCCESS'),
      bitfToTranslate('CHILDREN.DELAY_DIALOG.TM_SUCCESS'),
      bitfToTranslate('COMMON.TOAST_MESSAGES.ERROR'),
      bitfToTranslate('CHILDREN.DELAY_DIALOG.TM_ERROR'),
    ]);
  }

  onCancel() {
    this.dialogRef.close({ data: null, status: EBitfCloseEventStatus.CANCEL } as IBitfCloseEvent<any>);
  }
}
